import type { FC, PropsWithChildren } from 'react';
import { createContext, useState, useMemo, useContext } from 'react';

import { DEFAULT_VALUES, TABS_CONTENT } from 'app/config/urlParams.config';
import ConfigContext from 'app/common/ConfigContext';
import { isSingleBlockDisplay } from 'app/utils/style';

interface DisplayContextValue {
  displayedTab: Tab | null;
  displayedBlocks: Block[];
  singleBlockDisplay: boolean;
  setDisplayedTab: (tab: Tab) => void;
}

const DisplayContext = createContext<DisplayContextValue>({
  displayedTab: DEFAULT_VALUES.tab.at(0) || null,
  displayedBlocks: DEFAULT_VALUES.block,
  singleBlockDisplay: isSingleBlockDisplay(DEFAULT_VALUES.block, DEFAULT_VALUES.full_height),
  setDisplayedTab: () => null
});

const DisplayContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const { tab, block, full_height } = useContext(ConfigContext);

  const [displayedTab, setDisplayedTab] = useState<DisplayContextValue['displayedTab']>(
    tab.at(0) || null
  );

  const displayedBlocks = displayedTab ? TABS_CONTENT[displayedTab] : block;

  const singleBlockDisplay = isSingleBlockDisplay(displayedBlocks, full_height);

  const value = useMemo(
    () => ({ displayedTab, setDisplayedTab, displayedBlocks, singleBlockDisplay }),
    [displayedTab, displayedBlocks, setDisplayedTab, singleBlockDisplay]
  );

  return <DisplayContext.Provider value={value}>{children}</DisplayContext.Provider>;
};

DisplayContextProvider.displayName = 'DisplayContextProvider';
DisplayContext.displayName = 'DisplayContext';

export default DisplayContext;
export { DisplayContextProvider };
export type { DisplayContextValue };
